<template>
    <div class="rename-bill-form form" ref="renameBillFormElement">
        <div class="formrow">
            <div class="formcontrol">
                <label for="bill-name">{{ translations.renameBillModal.billNameLabel }}</label>
                <input
                    ref="billNameInputElement"
                    type="text"
                    id="bill-name"
                    name="bill-name"
                    autocomplete="off"
                    v-model="billName"
                    @input="onInput"
                    @keypress="onKeyPress"
                />
            </div>
        </div>

        <div class="btns">
            <AppButton class="warning rounded" @click="close">
                {{ trans.general.cancel }}
            </AppButton>
            <AppButton class="primary rounded" :disabled="isSubmitDisabled" @click="saveChanges">
                {{ trans.general.save }}
            </AppButton>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { trans } from "@/app/localization/Trans";
    import { Popup } from "@/app/ui/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { useBillStore } from "@/data/stores/bill";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { Logging } from "@/data/util/Logging";
    import { isNullOrWhitespace } from "@/data/util/String";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { FloatLabel } from "@/shared/ui/FloatLabel";
    import { computed, onMounted, ref, watch } from "vue";

    import AppButton from "@/app/components/elements/AppButton.vue";

    // define props
    const props = defineProps<{
        onClose: () => unknown;
    }>();

    type OriginalBillData = {
        id: string;
        billName: string | undefined;
        allocationHash: string;
    };

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
    const bill = useBillStore();
    const allocation = useTableAllocationStore();

    const renameBillFormElement = ref<HTMLElement | null>(null);
    const billNameInputElement = ref<HTMLElement | null>(null);

    // define html refs
    const isSaving = ref(false);

    const originalBill = ref<OriginalBillData>();
    const billName = ref<string | undefined>();

    const isBillNameChanged = computed(() => trim(billName.value) != trim(originalBill.value?.billName));
    const isSubmitDisabled = computed(
        () => isSaving.value || isNullOrWhitespace(billName.value) || !isBillNameChanged.value
    );

    const emit = defineEmits(["close"]);

    async function onKeyPress(e: KeyboardEvent) {
        if (!isSubmitDisabled.value && e.key === "Enter") {
            saveChanges();
        }
    }

    async function saveChanges() {
        isSaving.value = true;

        try {
            await operatorDao.updateBillName(bill.currentBill!.id, {
                name: trim(billName.value),
            });
            bill.updateBillName(bill.currentBill!.id, billName.value!);
            close();
        } catch (ex) {
            logError(ex);
            Popup.showWithOptions({
                message: translations.general.operationFailed,
            });
        } finally {
            isSaving.value = false;
        }
    }

    function trim(value?: string) {
        return (value ?? "").trim();
    }

    function close() {
        emit("close");
        props.onClose();
    }

    // need to add onInput because mobile chrome doesn't update input value in real time - while typing. only on blur
    function onInput(event) {
        billName.value = event.target.value;
    }

    watch(
        () => allocation.hash,
        () => {
            if (allocation.hash != originalBill.value!.allocationHash) {
                // if allocation has changed - close this modal
                Logging.LogInfo(
                    `Closing RenameBillModal because allocation has changed - previous ${originalBill.value!.allocationHash}, current ${allocation.hash}`
                );
                close();
            }
        }
    );

    onMounted(() => {
        originalBill.value = {
            id: bill.currentBill!.id,
            billName: bill.currentBill!.customName,
            allocationHash: allocation.hash,
        };
        billName.value = bill.currentBill!.customName;

        FloatLabel.bind($(renameBillFormElement.value!));

        window.setTimeout(() => {
            $(billNameInputElement.value!).focus();
        }, 300);
    });
</script>

<style lang="scss">
    .rename-bill-form {
        padding: 0 0 3.5em;

        .btns {
            display: flex;
            justify-content: space-between;
            margin-top: 0.75em;

            .app-button {
                font-size: 17px;
                flex-basis: calc(50% - 0.5em);
                line-height: calc(2.75em - 4px);
                border-radius: 1.325em !important;
            }
        }
    }
</style>
